import { useEffect } from 'react';

type PageSeoProps = {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  robots?: string;
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
};

const GLOBAL_SCHEMA_GRAPH = [
  {
    '@type': 'Organization',
    '@id': 'https://personalfinanceacademy.net/#organization',
    name: 'Personal Finance Academy',
    legalName: 'ITGER CORP LTD.',
    url: 'https://personalfinanceacademy.net/',
    logo: {
      '@type': 'ImageObject',
      '@id': 'https://personalfinanceacademy.net/#logo',
      url: 'https://personalfinanceacademy.net/personalfinanceacademy-logo-banner.webp',
      contentUrl: 'https://personalfinanceacademy.net/personalfinanceacademy-logo-banner.webp',
    },
    email: 'support@personalfinanceacademy.net',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Kalergi, 18',
      addressLocality: 'Egkomi, Nicosia',
      postalCode: '2400',
      addressCountry: 'Cyprus',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@personalfinanceacademy.net',
    },
  },
  {
    '@type': 'WebSite',
    '@id': 'https://personalfinanceacademy.net/#website',
    url: 'https://personalfinanceacademy.net/',
    name: 'Personal Finance Academy',
    publisher: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
  },
];

const PAGE_SCHEMA_BY_CANONICAL_URL: Record<string, Record<string, unknown>> = {
  'https://personalfinanceacademy.net/': {
    '@type': 'WebPage',
    '@id': 'https://personalfinanceacademy.net/#webpage',
    url: 'https://personalfinanceacademy.net/',
    name: 'Personal Finance Academy',
    description: 'Learn about personal finance and improve your financial knowledge with Personal Finance Academy.',
    isPartOf: {
      '@id': 'https://personalfinanceacademy.net/#website',
    },
    about: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
    publisher: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
  },
  'https://personalfinanceacademy.net/pricing/': {
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://personalfinanceacademy.net/pricing/#webpage',
        url: 'https://personalfinanceacademy.net/pricing/',
        name: 'Personal Finance Academy Pricing',
        description:
          'Compare Personal Finance Academy membership plans and choose a monthly plan that fits your learning and financial education needs.',
        isPartOf: {
          '@id': 'https://personalfinanceacademy.net/#website',
        },
        about: {
          '@id': 'https://personalfinanceacademy.net/#organization',
        },
        mainEntity: {
          '@id': 'https://personalfinanceacademy.net/pricing/#membership-plans',
        },
        breadcrumb: {
          '@id': 'https://personalfinanceacademy.net/pricing/#breadcrumb',
        },
        publisher: {
          '@id': 'https://personalfinanceacademy.net/#organization',
        },
      },
      {
        '@type': 'OfferCatalog',
        '@id': 'https://personalfinanceacademy.net/pricing/#membership-plans',
        name: 'Personal Finance Academy Membership Plans',
        itemListElement: [
          {
            '@type': 'Offer',
            '@id': 'https://personalfinanceacademy.net/pricing/#basic-plan-offer',
            name: 'Basic Monthly Membership',
            url: 'https://personalfinanceacademy.net/pricing/',
            price: '29.00',
            priceCurrency: 'EUR',
            seller: {
              '@id': 'https://personalfinanceacademy.net/#organization',
            },
            itemOffered: {
              '@type': 'Service',
              '@id': 'https://personalfinanceacademy.net/pricing/#basic-plan',
              name: 'Personal Finance Academy Basic Plan',
              image: 'https://personalfinanceacademy.net/pricing-basic-card.webp',
              serviceType: 'Monthly financial education membership',
              description:
                'Cancel anytime. Access lifestyle courses. Business courses and unlimited access are not included.',
              provider: {
                '@id': 'https://personalfinanceacademy.net/#organization',
              },
            },
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '29.00',
              priceCurrency: 'EUR',
              unitText: 'month',
              billingDuration: 'P1M',
            },
          },
          {
            '@type': 'Offer',
            '@id': 'https://personalfinanceacademy.net/pricing/#standard-plan-offer',
            name: 'Standard Monthly Membership',
            url: 'https://personalfinanceacademy.net/pricing/',
            price: '59.00',
            priceCurrency: 'EUR',
            seller: {
              '@id': 'https://personalfinanceacademy.net/#organization',
            },
            itemOffered: {
              '@type': 'Service',
              '@id': 'https://personalfinanceacademy.net/pricing/#standard-plan',
              name: 'Personal Finance Academy Standard Plan',
              image: 'https://personalfinanceacademy.net/pricing-standard-card.webp',
              serviceType: 'Monthly financial education membership',
              description: 'Cancel anytime. Access lifestyle courses. Access business courses.',
              provider: {
                '@id': 'https://personalfinanceacademy.net/#organization',
              },
            },
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '59.00',
              priceCurrency: 'EUR',
              unitText: 'month',
              billingDuration: 'P1M',
            },
          },
          {
            '@type': 'Offer',
            '@id': 'https://personalfinanceacademy.net/pricing/#premium-plan-offer',
            name: 'Premium Monthly Membership',
            url: 'https://personalfinanceacademy.net/pricing/',
            price: '99.00',
            priceCurrency: 'EUR',
            seller: {
              '@id': 'https://personalfinanceacademy.net/#organization',
            },
            itemOffered: {
              '@type': 'Service',
              '@id': 'https://personalfinanceacademy.net/pricing/#premium-plan',
              name: 'Personal Finance Academy Premium Plan',
              image: 'https://personalfinanceacademy.net/pricing-premium-card.webp',
              serviceType: 'Monthly financial education membership',
              description: 'Cancel anytime. Access lifestyle courses. Access business courses. Unlimited access.',
              provider: {
                '@id': 'https://personalfinanceacademy.net/#organization',
              },
            },
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '99.00',
              priceCurrency: 'EUR',
              unitText: 'month',
              billingDuration: 'P1M',
            },
          },
        ],
      },
      {
        '@type': 'BreadcrumbList',
        '@id': 'https://personalfinanceacademy.net/pricing/#breadcrumb',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://personalfinanceacademy.net/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Pricing',
            item: 'https://personalfinanceacademy.net/pricing/',
          },
        ],
      },
    ],
  },
  'https://personalfinanceacademy.net/unsubscribe/': {
    '@type': 'WebPage',
    '@id': 'https://personalfinanceacademy.net/unsubscribe/#webpage',
    url: 'https://personalfinanceacademy.net/unsubscribe/',
    name: 'Cancel Your Subscription | Personal Finance Academy',
    description: 'Cancel your Personal Finance Academy subscription and manage your membership preferences.',
    isPartOf: {
      '@id': 'https://personalfinanceacademy.net/#website',
    },
    about: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
    publisher: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
  },
  'https://personalfinanceacademy.net/signup/': {
    '@type': 'WebPage',
    '@id': 'https://personalfinanceacademy.net/signup/#webpage',
    url: 'https://personalfinanceacademy.net/signup/',
    name: 'Sign Up | Personal Finance Academy',
    description:
      'Create a Personal Finance Academy account and get started with access to personal finance resources, educational content, and online learning materials.',
    isPartOf: {
      '@id': 'https://personalfinanceacademy.net/#website',
    },
    about: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
    publisher: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
  },
  'https://personalfinanceacademy.net/terms-and-conditions/': {
    '@type': 'WebPage',
    '@id': 'https://personalfinanceacademy.net/terms-and-conditions/#webpage',
    url: 'https://personalfinanceacademy.net/terms-and-conditions/',
    name: 'Terms and Conditions | Personal Finance Academy',
    description:
      'Read the Terms and Conditions governing the Personal Finance Academy website, memberships, educational content, and related online services.',
    isPartOf: {
      '@id': 'https://personalfinanceacademy.net/#website',
    },
    about: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
    publisher: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
  },
  'https://personalfinanceacademy.net/privacy-policy/': {
    '@type': 'WebPage',
    '@id': 'https://personalfinanceacademy.net/privacy-policy/#webpage',
    url: 'https://personalfinanceacademy.net/privacy-policy/',
    name: 'Privacy Policy | Personal Finance Academy',
    description:
      'Read the Personal Finance Academy Privacy Policy to learn how personal information is collected, used, protected, and processed.',
    isPartOf: {
      '@id': 'https://personalfinanceacademy.net/#website',
    },
    about: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
    publisher: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
  },
  'https://personalfinanceacademy.net/imprint/': {
    '@type': 'WebPage',
    '@id': 'https://personalfinanceacademy.net/imprint/#webpage',
    url: 'https://personalfinanceacademy.net/imprint/',
    name: 'Imprint | Personal Finance Academy',
    description:
      "Personal Finance Academy's imprint provides legal disclosure information, company details, contact information, and other required information about the website and its operator.",
    isPartOf: {
      '@id': 'https://personalfinanceacademy.net/#website',
    },
    about: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
    publisher: {
      '@id': 'https://personalfinanceacademy.net/#organization',
    },
  },
};

function getPageSchema(canonicalUrl?: string, schema?: Record<string, unknown> | Array<Record<string, unknown>>) {
  return canonicalUrl ? PAGE_SCHEMA_BY_CANONICAL_URL[canonicalUrl] || schema : schema;
}

function getSchemaGraph(schema?: Record<string, unknown> | Array<Record<string, unknown>>, canonicalUrl?: string) {
  const selectedSchema = getPageSchema(canonicalUrl, schema);

  if (!selectedSchema) {
    return GLOBAL_SCHEMA_GRAPH;
  }

  if (Array.isArray(selectedSchema)) {
    return [...GLOBAL_SCHEMA_GRAPH, ...selectedSchema];
  }

  if (Array.isArray(selectedSchema['@graph'])) {
    return [...GLOBAL_SCHEMA_GRAPH, ...selectedSchema['@graph']];
  }

  return [...GLOBAL_SCHEMA_GRAPH, selectedSchema];
}

function buildStructuredData(schema?: Record<string, unknown> | Array<Record<string, unknown>>, canonicalUrl?: string) {
  return {
    '@context': 'https://schema.org',
    '@graph': getSchemaGraph(schema, canonicalUrl),
  };
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function removeMeta(attribute: 'name' | 'property', key: string) {
  document.head.querySelector(`meta[${attribute}="${key}"]`)?.remove();
}

function upsertCanonical(url: string) {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', url);
}

function upsertJsonLd(schema: Record<string, unknown> | Array<Record<string, unknown>>) {
  const scriptId = 'page-json-ld';
  let tag = document.head.querySelector<HTMLScriptElement>(`script#${scriptId}`);

  if (!tag) {
    tag = document.createElement('script');
    tag.id = scriptId;
    tag.type = 'application/ld+json';
    document.head.appendChild(tag);
  }

  tag.textContent = JSON.stringify(schema);
}

export default function PageSeo({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  robots,
  schema,
}: PageSeoProps) {
  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', ogTitle || title);
    upsertMeta('property', 'og:description', ogDescription || description);
    upsertMeta('property', 'og:type', ogType);

    if (robots) {
      upsertMeta('name', 'robots', robots);
    } else {
      removeMeta('name', 'robots');
    }

    upsertMeta('property', 'og:image', 'https://personalfinanceacademy.net/personalfinanceacademy-logo-banner.webp');
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');

    if (canonicalUrl) {
      upsertCanonical(canonicalUrl);
      upsertMeta('property', 'og:url', canonicalUrl);
    }

    upsertJsonLd(buildStructuredData(schema, canonicalUrl));

    return () => {
      const script = document.head.querySelector<HTMLScriptElement>('script#page-json-ld');
      if (script) {
        script.remove();
      }
    };
  }, [canonicalUrl, description, ogDescription, ogTitle, ogType, robots, schema, title]);

  return null;
}
